/**
 * How much of the layout the on-screen keyboard covers, published as a CSS variable.
 *
 * Nothing about the keyboard reaches CSS on its own: the layout viewport does not
 * shrink for it, so `100dvh` keeps reporting the whole screen and anything docked to the
 * bottom edge — the primary button on both sign-in screens — ends up underneath the
 * keys. Two sources answer it, because neither one answers everywhere:
 *
 *  - **`visualViewport`** is how a browser says it. It is also the only source that
 *    works on the web, where there is no plugin.
 *  - **The Keyboard plugin** is how the native shell says it. WKWebView draws the
 *    keyboard *over* the web view without touching either viewport, so on iOS the
 *    measurement above stays at zero however far up the keys come — which is exactly
 *    the bug this file exists to fix.
 *
 * Whichever platform resizes for the keyboard has already moved the layout, so the
 * plugin's height is only worth what the viewport did *not* already give back:
 * `remainingKeyboardInset` subtracts that, and the larger of the two answers wins.
 * Android, which shrinks the web view as the keyboard animates in, therefore settles
 * at zero rather than pushing the content up twice.
 *
 * Screens opt in with `var(--elyxeer-keyboard-inset)`; everything else ignores it.
 */

import { Keyboard } from "@capacitor/keyboard";

import { isNative } from "./capacitor";

/** The variable every screen reads. Defaulted at each call site, never declared. */
export const KEYBOARD_INSET_VAR = "--elyxeer-keyboard-inset";

/**
 * px — below this the gap is browser chrome, not a keyboard: a mobile address bar
 * collapsing changes the visual viewport by a few dozen pixels. No keyboard is
 * this short.
 */
const KEYBOARD_MIN_HEIGHT = 80;

/**
 * What is left of a keyboard the platform has already made room for.
 *
 * @param keyboardHeight - What the plugin says the keyboard covers.
 * @param baselineHeight - The viewport with no keyboard up: what `dvh` is sized on.
 * @param viewportHeight - The viewport now. Smaller means the platform resized, and
 *   the layout moved with it.
 */
export function remainingKeyboardInset(
  keyboardHeight: number,
  baselineHeight: number,
  viewportHeight: number,
): number {
  const madeRoom = Math.max(0, baselineHeight - viewportHeight);
  return Math.max(0, keyboardHeight - madeRoom);
}

/** Start publishing the inset. Returns the teardown. */
export function trackKeyboardInset(): () => void {
  const viewport = window.visualViewport;

  /** What the plugin last reported; 0 whenever the keyboard is down. */
  let keyboardHeight = 0;
  /** The viewport as the layout knows it, re-read whenever no keyboard is up. */
  let baseline = window.innerHeight;

  const apply = () => {
    if (keyboardHeight === 0) baseline = window.innerHeight;

    // What the browser admits to covering. The `offsetTop` term matters as much as the
    // height: iOS scrolls the page to reveal a focused field, and the padding has to
    // come off by however much that scroll already did.
    const measured = viewport
      ? window.innerHeight - viewport.height - viewport.offsetTop
      : 0;
    const reported = remainingKeyboardInset(
      keyboardHeight,
      baseline,
      window.innerHeight,
    );

    const inset = Math.max(measured, reported);
    document.documentElement.style.setProperty(
      KEYBOARD_INSET_VAR,
      `${inset < KEYBOARD_MIN_HEIGHT ? 0 : Math.round(inset)}px`,
    );
  };

  const setKeyboardHeight = (height: number) => {
    keyboardHeight = height;
    apply();
  };

  // `willShow` moves the layout with the keyboard's own animation rather than after it;
  // `didShow` is what corrects the number on a platform that resized in between.
  const listeners = isNative()
    ? [
        Keyboard.addListener("keyboardWillShow", (info) =>
          setKeyboardHeight(info.keyboardHeight),
        ),
        Keyboard.addListener("keyboardDidShow", (info) =>
          setKeyboardHeight(info.keyboardHeight),
        ),
        Keyboard.addListener("keyboardWillHide", () => setKeyboardHeight(0)),
      ]
    : [];

  apply();
  viewport?.addEventListener("resize", apply);
  viewport?.addEventListener("scroll", apply);
  // A web view that resizes for the keyboard reports it here, and nowhere else.
  window.addEventListener("resize", apply);

  return () => {
    viewport?.removeEventListener("resize", apply);
    viewport?.removeEventListener("scroll", apply);
    window.removeEventListener("resize", apply);
    for (const listener of listeners)
      void listener.then((handle) => handle.remove());
    document.documentElement.style.removeProperty(KEYBOARD_INSET_VAR);
  };
}
